import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Slide,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  NodeSelect,
  NodeMentionEditor,
  NodeSection,
  NodeRadioGroup,
  NodeAddButton,
} from '../../../NodeUI';
import { NODE_UI, inputSx, stopPropagation } from '../../../NodeUI/nodeUiStyles';
import { send_request } from '../../../../utils/Request';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FUNCTION_OPTIONS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
  { value: 'gt', label: 'greater than' },
  { value: 'gte', label: 'greater than or equal to' },
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'less than or equal to' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'ends_with', label: 'ends with' },
];

const LOGIC_OPTIONS = [
  { value: 'and', label: 'AND' },
  { value: 'or', label: 'OR' },
];

const noValueOperators = new Set(['empty', 'not_empty']);

const createEmptyRule = (combinator = undefined) => ({
  field: '',
  operator: 'eq',
  value: '',
  ...(combinator ? { combinator } : {}),
});

const ConditionRow = styled('div')(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  padding: NODE_UI.spacing.md,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  gap: NODE_UI.spacing.sm,
  position: 'relative',
  '&:hover .condition-actions': { opacity: 1 },
}));

const ActionsRow = styled('div')({
  position: 'absolute',
  top: 6,
  right: 6,
  display: 'flex',
  gap: 2,
  opacity: 0,
  transition: 'opacity 0.15s',
});

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: 3,
  borderRadius: NODE_UI.borderRadiusSm,
  color: theme.palette.text.secondary,
  '&:hover': { color: theme.palette.error.main },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({ ...inputSx(theme) }));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

function EdgeConditionDialog({
  isOpen,
  onClose,
  onConfirm,
  initialEdgeCondition = null,
  projectId,
  sourceComponentId,
  gatewayType = null,
}) {
  const isExclusive = gatewayType === 'exclusive';
  const [priority, setPriority] = useState(1);
  const [isDefault, setIsDefault] = useState(false);
  const [rules, setRules] = useState([createEmptyRule()]);

  const [availablePlaceholders, setAvailablePlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);

  useEffect(() => {
    const fetchPlaceholders = async () => {
      if (!projectId || !sourceComponentId) return;
      setLoadingPlaceholders(true);
      try {
        const url = `component-service/placeholder/${projectId}/${sourceComponentId}`;
        const response = await send_request(url, '', '', 'get');
        if (response?.data?.placeholders) {
          setAvailablePlaceholders(response.data.placeholders);
        }
      } catch (err) {
        // Silently fail
      } finally {
        setLoadingPlaceholders(false);
      }
    };
    if (isOpen) fetchPlaceholders();
  }, [isOpen, projectId, sourceComponentId]);

  useEffect(() => {
    if (isOpen) {
      if (initialEdgeCondition) {
        setPriority(initialEdgeCondition.priority || 1);
        setIsDefault(initialEdgeCondition.isDefault || false);
        setRules(initialEdgeCondition.rules?.length > 0 ? initialEdgeCondition.rules : [createEmptyRule()]);
      } else {
        setPriority(1);
        setIsDefault(false);
        setRules([createEmptyRule()]);
      }
    }
  }, [isOpen, initialEdgeCondition]);

  const updateRule = useCallback((index, field, value) => {
    setRules(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }, []);

  const addRule = useCallback(() => {
    setRules(prev => [...prev, createEmptyRule('and')]);
  }, []);

  const removeRule = useCallback((index) => {
    setRules(prev => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      if (next.length > 0) {
        const { combinator, ...rest } = next[0];
        next[0] = rest;
      }
      return next;
    });
  }, []);

  const handleConfirm = () => {
    const edgeCondition = { priority, isDefault };
    if (!isDefault) {
      edgeCondition.rules = rules.filter(r => r.field.trim() !== '');
    }
    onConfirm(edgeCondition);
  };

  const isValid = isDefault || rules.some(r => r.field.trim() !== '');

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeviceHubIcon sx={{ color: 'text.secondary' }} />
          <span>Edge Condition</span>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: NODE_UI.spacing.lg }}>
        <Typography variant="body2" color="text.secondary">
          {isExclusive
            ? <>The workflow will follow this path <strong>only</strong> when all conditions below are met.</>
            : <>This path will be followed when the conditions below are met. All matching paths run.</>
          }
        </Typography>

        {isExclusive && (
          <div>
            <Label>Evaluation order</Label>
            <StyledTextField
              type="number"
              fullWidth
              size="small"
              value={priority}
              onChange={(e) => setPriority(parseInt(e.target.value, 10) || 1)}
              helperText="Edges are checked in order (1 = first). The first matching edge wins."
              inputProps={{ min: 1, max: 99 }}
            />
          </div>
        )}

        {isExclusive && (
          <FormControlLabel
            control={
              <Checkbox
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography sx={{ fontSize: NODE_UI.fontSize.value, fontWeight: NODE_UI.fontWeight.label }}>
                Use as fallback (taken when no other edges match)
              </Typography>
            }
          />
        )}

        {!isDefault && (
          <NodeSection title="Take this path when...">
            {loadingPlaceholders ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} />
                <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: 'text.secondary' }}>
                  Loading placeholders...
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: 'text.secondary' }}>
                Type <strong>$</strong> to insert a placeholder value
              </Typography>
            )}

            {rules.map((rule, index) => (
              <React.Fragment key={index}>
                {index > 0 && (
                  <NodeRadioGroup
                    value={rule.combinator || 'and'}
                    onChange={(val) => updateRule(index, 'combinator', val)}
                    options={LOGIC_OPTIONS}
                    row
                  />
                )}

                <ConditionRow>
                  <ActionsRow className="condition-actions">
                    {rules.length > 1 && (
                      <ActionButton
                        size="small"
                        onClick={(e) => { stopPropagation(e); removeRule(index); }}
                        onMouseDown={stopPropagation}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </ActionButton>
                    )}
                  </ActionsRow>

                  <NodeMentionEditor
                    label="Check this value"
                    value={rule.field || ''}
                    onChange={(val) => updateRule(index, 'field', val)}
                    availablePlaceholders={availablePlaceholders}
                    placeholder="$ for placeholder"
                    variant="minimal"
                  />

                  <NodeSelect
                    label="Is"
                    value={rule.operator || 'eq'}
                    onChange={(val) => updateRule(index, 'operator', val)}
                    options={FUNCTION_OPTIONS}
                  />

                  {!noValueOperators.has(rule.operator) && (
                    <NodeMentionEditor
                      label="This value"
                      value={rule.value || ''}
                      onChange={(val) => updateRule(index, 'value', val)}
                      availablePlaceholders={availablePlaceholders}
                      placeholder="Value or $ for placeholder"
                      variant="minimal"
                    />
                  )}
                </ConditionRow>
              </React.Fragment>
            ))}

            <NodeAddButton label="Add Condition" onClick={addRule} />
          </NodeSection>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" size="small">Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" size="small" disabled={!isValid}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EdgeConditionDialog;
